import {Component, OnDestroy, OnInit} from '@angular/core';
import {Router} from '@angular/router';
import {ProjectService} from '@catcore/services';
import {Project} from '@catcore/types/api-v2';
import {ProjectDetailsService} from '@catdetails/services';
import {ValidationState} from '@catmodules/widgets/shared/states';
import {Actions as actionItems} from '@catproject/actionbar-items';
import {ExplorerItem, TreeNodeInfo} from '@catui/types';
import {isNullOrUndefined} from 'util';
import {sortByLabelFunc} from "@catcore/utils";

const emptyActionItems = [];
const unsetActiveItem = null;

/**
 * Container component to
 * - display a list of available projects
 * - provide controls for handling project list and a direct sub-navigation
 */
@Component({
  templateUrl: './project-list.component.html',
})
export class ProjectListComponent implements OnInit, OnDestroy {

  /**
   * A list of all currently available and useable Project items
   * @type {any[]}
   */
  projects: Project[] = [];

  /**
   * A list of items to display all useable projects in a navigation tree
   * @type {any[]}
   */
  explorerItems: ExplorerItem[] = [];

  /**
   * A list of static navigation items belonging and getting useable when selecting a single project
   * @type {any[]}
   */
  actionbarItems = emptyActionItems;

  /**
   * The currently selected ExplorerItem in the project navigation list
   * @type {null}
   */
  activeProjectItem: ExplorerItem = null;

  /**
   * A option flag to tell the project navigation tree to allow to display a according control component
   * @type {boolean}
   */
  doAllowCreate = true;

  /**
   * The current selected Project
   */
  private currentProject: Project;

  /**
   * A unmodifyable buffer value to be used a when accessing streaming-data
   * @type {number}
   */
  private readonly itemUpdateTimeout = 1;

  /**
   * Providing the required service / injectabled.
   * @param {ProjectService} projectService
   * @param {ProjectDetailsService} projectDetailsService
   * @param {Router} router
   */
  constructor(private projectService: ProjectService,
              private projectDetailsService: ProjectDetailsService,
              private router: Router) {

  }

  /**
   * Callback handler to be used in the project navigation to navigate to a view for creating a new project
   */
  onAddOrCreate() {
    this.router.navigate(['projects', 'new']);
  }

  /**
   * Remove all data that was used to change display behavipour based on a selected project so that after reinitializing this component no
   * weird stuff happens with somewhere stored data.
   */
  ngOnDestroy() {
    this.projectService.setCurrentProject(null);
    this.projectService.resetCurrent();
  }

  /**
   * Subscribe to the current in use project and execute continuously related behaviours as soon as new project information are available.
   * Subscribe to all useable / available projects and execute continuously related behaviours.
   */
  ngOnInit() {
    this.projectService
      .getCurrentProject()
      .delay(100)
      .subscribe((currentProject) => {
        this.currentProject = currentProject;
        this.toggleExplorerItem(currentProject);

        if (this.activeProjectItem) {
          this.activeProjectItem.label = buildProjectExplorerItem(currentProject).label;
        }

        this.projectDetailsService
          .getProjectDetails()
          .delay(this.itemUpdateTimeout)
          .subscribe((projectDetails) => {
            this.toggleActionItemStates(this.actionbarItems, projectDetails);
          });

      });

    this.projectService
      .list$
      .subscribe((projectList) => {
        this.projects = projectList;
        this.updateExplorerItems(this.projects);
        this.refreshActiveItem(this.currentProject);

      });

    this.projectService.refreshList();
  }

  /**
   * Callback for when a project is selected in the navigation tree.
   * Navigates to the selected project properties component
   * @param {TreeNodeInfo} nodeInfo
   */
  onActivateProjectItem(nodeInfo: TreeNodeInfo) {
    console.log('active item:', nodeInfo, this.activeProjectItem);
    this.router.navigate(['projects', nodeInfo.item.value, 'properties']);
  }

  /**
   * Callback for one of the direct subnavigation item.
   * Navigates to the target defined in the items value object
   * @param item
   */
  onActivateSubItem(item) {
    const routeTarget = item.routeTarget(this.activeProjectItem.value);
    this.router.navigate(routeTarget);
  }

  /**
   * Based on the provided state information for every available ui section of the current project, the states of the available
   * items for the actionbar are toggled.
   * @param items
   * @param projectDetails
   */
  private toggleActionItemStates(items, projectDetails) {
    let activeIndicatorPropertyName = 'isActive';
    items.map((itemInfo) => {
      const propertyName = itemInfo.reactToState.property;

      if (!isNullOrUndefined(itemInfo.reactToState.activePropertyName)) {
        activeIndicatorPropertyName = itemInfo.reactToState.activePropertyName;
      }

      let propExists = !isNullOrUndefined(itemInfo.reactToState);

      propExists = propExists && Object
        .keys(projectDetails)
        .includes(itemInfo.reactToState.property);

      if (propExists) {
        itemInfo.isActive = projectDetails[propertyName][activeIndicatorPropertyName];
      }
    });
  }

  /**
   * Lookup and set the matching ExplorerItem of the project navigation to the matching state of the current project
   * @param projectData
   */
  private toggleExplorerItem(projectData) {
    if (projectData && !isNullOrUndefined(projectData) && !isNullOrUndefined(projectData.id)) {
      this.actionbarItems = [];
      actionItems.forEach((actionItem) => {
        let isApplicableFunctionDefined = (typeof actionItem.isApplicableForProject === 'function');
        if (isApplicableFunctionDefined === false || (isApplicableFunctionDefined === true
            && actionItem.isApplicableForProject(projectData) === true)) {
          this.actionbarItems.push(actionItem);
        }
      });
      this.activeProjectItem = findExplorerItemForProject(this.explorerItems, projectData);
    } else {
      this.actionbarItems = emptyActionItems;
      this.activeProjectItem = unsetActiveItem;
    }
  }

  /**
   * Create a new list of items for the current project navigation list based on the provided list of projects
   * @param {any[]} projectItems
   */
  private updateExplorerItems(projectItems: any[]) {
    this.explorerItems = [];
    projectItems.forEach((projectItem) => {
      this.explorerItems.push(buildProjectExplorerItem(projectItem));
    });
    this.explorerItems.sort(sortByLabelFunc);
  }

  /**
   * Set the current active item for the project navigation based on the provided project and the currently available items
   * of the navigation itself.
   *
   * @param {Project} project
   */
  private refreshActiveItem(project: Project) {
    this.activeProjectItem = findExplorerItemForProject(this.explorerItems, project);
  }
}

/**
 * Returns a matching ExplorerItem for the provided Project model
 * @param {ExplorerItem[]} explorerItems
 * @param {Project} projectData
 * @returns {ExplorerItem}
 */
const findExplorerItemForProject = (explorerItems: ExplorerItem[], projectData: Project): ExplorerItem => {
  const items = [...explorerItems];
  let returnable: ExplorerItem = null;

  if (!isNullOrUndefined(projectData)) {
    returnable = items.filter((item: ExplorerItem) => item.value === projectData.id).pop();
  }

  return returnable;
};

/**
 * Create and returns a ExplorerItem for a Project model
 * @param project
 * @returns {ExplorerItem}
 */
const buildProjectExplorerItem = (project): ExplorerItem => {
  return {
    label: project.name,
    value: project.id,
    expanded: false,
    stateInfo: {
      state: project.valid ? ValidationState.VALID : ValidationState.FAILED,
    },
    allowedActions: [],
  };
};



// WEBPACK FOOTER //
// /agent/workspace/CUS-LHT_NICE_nto_nto_CAT_release@2/app/Resources/Public/src/app/modules/project/components/project-list/project-list.component.ts